import _isEmpty from 'underscore/cjs/isEmpty.js'

import {
  SQPP_FLOW,
  SQPP_SLUG_MAP,
  CONTROL_STEP_TO_SLUG
} from '~/constants/funnel/energy-sqpp'

/**
 * HI-1430 — pure navigation logic for the Energy SQPP funnel.
 *
 * Everything here takes a plain flattened model (`flattenObj(form, 1)`) and
 * returns a plain value. No stores, no router, no component instance — the 10
 * leaf paths are exhaustively unit-testable without mounting anything.
 */

/**
 * Has this row been answered?
 *
 * `address` and `postcode` hold objects; selections hold strings; the contact
 * row has no single field, so it is never "answered" here — which is what makes
 * `firstIncompleteSlug` fall through to it once everything else is done.
 */
export function isRowAnswered (model, row) {
  if (!row?.field) {
    return false
  }

  const value = model?.[row.field]

  if (typeof value === 'number' || typeof value === 'boolean') {
    return true
  }

  return !_isEmpty(value)
}

/**
 * Does this row stop the visitor going past it?
 *
 * A `skippable` row never does. `Not sure? Skip for now` writes `''` — the
 * value the CRM already gets for an unasked retailer — so a skipped row is
 * indistinguishable from an unanswered one. Gating on `isRowAnswered` alone
 * bounced every skip straight back to the retailer screen (HI-1430 QA #1).
 *
 * Forward navigation is unaffected: `nextSlug` still walks every visible row,
 * so the question is always asked once on the way through.
 */
export function isRowSatisfied (model, row) {
  return Boolean(row?.skippable) || isRowAnswered(model, row)
}

/**
 * The ordered rows this visitor actually sees, given their answers so far.
 * Skipped rows are absent from the array, so `i - 1` is always the previous
 * question *on this path*.
 */
export function visiblePath (model = {}) {
  return SQPP_FLOW.filter(row => row.showIf(model))
}

export function visibleSlugs (model = {}) {
  return visiblePath(model).map(row => row.slug)
}

export function nextSlug (model = {}, slug) {
  const path = visiblePath(model)
  const index = path.findIndex(row => row.slug === slug)

  if (index === -1 || index === path.length - 1) {
    return null
  }

  return path[index + 1].slug
}

export function prevSlug (model = {}, slug) {
  const path = visiblePath(model)
  const index = path.findIndex(row => row.slug === slug)

  if (index <= 0) {
    return null
  }

  return path[index - 1].slug
}

/**
 * The first question on this visitor's path they have not answered — where a
 * deep link or a failed reachability check sends them. Optional (skippable)
 * rows are passed over, so a skipped retailer is never the bounce target.
 */
export function firstIncompleteSlug (model = {}) {
  const path = visiblePath(model)
  const row = path.find(item => !isRowSatisfied(model, item))

  return (row ?? path[path.length - 1]).slug
}

/**
 * Deep-link guard. Replaces the control's `VALIDATE_PREVIOUS_STEPS`, which is
 * far too weak here (it passes if *any* bucket is filled).
 *
 * Two ways to fail: the slug is not on this visitor's path at all, or a
 * required question before it is still unanswered.
 */
export function isSlugReachable (model = {}, slug) {
  const path = visiblePath(model)
  const index = path.findIndex(row => row.slug === slug)

  if (index === -1) {
    return false
  }

  return path.slice(0, index).every(row => isRowSatisfied(model, row))
}

/**
 * The single invariant: if a question is not on your current path, its answer
 * is discarded.
 *
 * Mutates `form` (the nested `{ stepN: { field } }` store object) in place and
 * returns the list of `stepN.field` keys it cleared, so callers and tests can
 * assert on exactly what moved.
 */
export function pruneOffPathAnswers (model = {}, form = {}) {
  const onPath = new Set(visibleSlugs(model))
  const cleared = []

  SQPP_FLOW.forEach(row => {
    if (onPath.has(row.slug) || !row.step) {
      return
    }

    const bucket = form?.[row.step]
    if (!bucket) {
      return
    }

    const fields = [row.field, ...(row.clearOnLeave ?? [])].filter(Boolean)

    fields.forEach(field => {
      if (!Object.hasOwnProperty.call(bucket, field)) {
        return
      }

      if (bucket[field] === '' || bucket[field] === undefined) {
        return
      }

      bucket[field] = ''
      cleared.push(`${row.step}.${field}`)
    })
  })

  return cleared
}

/**
 * Control step -> variant slug.
 *
 * Model-aware because `step2`, `step7` and `step8` are ambiguous. Server-side
 * there is no model, so it degrades to the static column — correct for `step1`,
 * the only step a cold entry uses.
 *
 * Deliberately NOT `firstIncompleteSlug` for every step: a visitor going Back
 * to `step3` to change their account type must land on `home-or-business`, not
 * be shunted forward to wherever they had got to.
 */
export function controlStepToSlug (step, model = null) {
  if (!Object.hasOwnProperty.call(CONTROL_STEP_TO_SLUG, step)) {
    return null
  }

  if (!model) {
    return CONTROL_STEP_TO_SLUG[step]
  }

  if (step === 'step2') {
    return model.sales_type === 'Move In' ? 'moving-date' : 'moving'
  }

  if (step === 'step7') {
    return model.account_type === 'SME' ? 'postcode' : 'address'
  }

  // step5/step6 are the bill-upload screens the variant omits, and step8
  // bundles five questions — in all three cases the best answer is "wherever
  // this visitor actually got to".
  if (step === 'step5' || step === 'step6' || step === 'step8') {
    return firstIncompleteSlug(model)
  }

  return CONTROL_STEP_TO_SLUG[step]
}

export function isSqppSlug (slug) {
  return Object.hasOwnProperty.call(SQPP_SLUG_MAP, slug)
}

export function getSqppRow (slug) {
  return SQPP_SLUG_MAP[slug] ?? null
}

/**
 * Stage label for the horizontal stepper, straight off the row.
 */
export function stageForSlug (slug) {
  return getSqppRow(slug)?.stage ?? null
}

/*
 * ── Analytics (HI-1430, tasks/HI-1430/HI-1430-analytics.md) ────────────────
 */

// The retailer tiles write the provider OBJECT, the dropdown writes the plain
// string. Same rule the page's `normaliseRetailers` applies on the way to the
// store, so analytics and the CRM see the same name.
function retailerName (value) {
  if (value && typeof value === 'object') {
    return value.name ?? value.code ?? ''
  }

  return typeof value === 'string' ? value : ''
}

// `DD/MM/YYYY` is what AtomsDatepicker writes; a Date is accepted too. Mirrors
// the `YYYY-MM-DD` that `leadParams` sends, without needing dayjs's
// customParseFormat plugin.
function isoDate (value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const pad = n => String(n).padStart(2, '0')
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`
  }

  const match = typeof value === 'string' && value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (match) {
    return `${match[3]}-${match[2]}-${match[1]}`
  }

  return typeof value === 'string' ? value : ''
}

/**
 * `MoleculesPostcode` stores `{ suburb, state, postcode }`. Analytics only ever
 * gets the digits: suburb + state would narrow the location further.
 */
export function sqppPostcodeDigits (value) {
  const raw = value && typeof value === 'object' ? value.postcode : value
  return String(raw ?? '').replace(/\D/g, '')
}

// Fields that never ride the pageview: a full residential address, a business
// name (can identify a sole trader), and `life_support` — a medical-equipment
// flag held back until analytics confirms it is wanted (D5).
const PAGEVIEW_OMITTED_FIELDS = new Set(['address', 'business_name', 'life_support'])

/**
 * The "previous answer" payload for the NEXT pageview (D5).
 *
 * Returns only the answered row's own field — never the whole `stepN` bucket,
 * which HI flattens wholesale and which here would carry the Google address
 * object and every earlier step8 answer on each screen. `{}` means "nothing to
 * send", and the caller still emits it so a stale payload is cleared.
 *
 * Takes the FLATTENED model, like every other helper in this file.
 */
export function buildSqppPageviewPayload (row, model = {}) {
  const field = row?.field

  if (!field || PAGEVIEW_OMITTED_FIELDS.has(field)) {
    return {}
  }

  const value = model?.[field]

  if (row.component === 'OrganismsProviderLogoSelection') {
    const name = retailerName(value)

    // A forward move off a retailer screen with no name can only be the skip
    // link — Continue does not validate an empty retailer.
    return name ? { [field]: name } : { [field]: '', [`${field}_skipped`]: true }
  }

  if (field === 'postcode') {
    const digits = sqppPostcodeDigits(value)
    return digits ? { postcode: digits } : {}
  }

  if (field === 'moving_date') {
    const date = isoDate(value)
    return date ? { moving_date: date } : {}
  }

  if (typeof value === 'string' && value !== '') {
    return { [field]: value }
  }

  return {}
}

/**
 * Visible label for an answer, used as `elementInnerText` on `Button Clicked`.
 * Options resolve against the flow table; retailers resolve to their name.
 * Falls back to '' — the internalTrackingId is what identifies the click.
 */
export function sqppOptionLabel (row, value) {
  if (row?.component === 'OrganismsProviderLogoSelection') {
    return retailerName(value)
  }

  const match = (row?.props?.options ?? []).find(option => option?.value === value)

  return match?.label ?? ''
}
